import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ServerContextService } from '../../../../feature-vault/src/lib/servers/server-context.service';
import type {
  FolderPermission,
  FolderPermissionCreatePayload,
  ModulePermission,
  ModulePermissionCreatePayload,
  ModulePermissionListParams,
  ModulePermissionUpdatePayload,
} from '../../../../domain/src/lib/module-permissions/module-permission.model';
import {
  resolveTenantId,
  tenantHeaders,
  withTenantParam,
} from '../shared/tenant-context.util';

@Injectable({ providedIn: 'root' })
export class ModulePermissionsService {
  private readonly http = inject(HttpClient);
  private readonly serverContext = inject(ServerContextService);
  private readonly basePath = '/api/module-permissions';

  list(params?: ModulePermissionListParams): Observable<ModulePermission[]> {
    const tenantId = resolveTenantId(this.serverContext);
    let httpParams = withTenantParam(tenantId) ?? new HttpParams();
    if (params?.user_id) httpParams = httpParams.set('user_id', params.user_id);
    if (params?.tenant_id) httpParams = httpParams.set('tenant_id', params.tenant_id);
    if (params?.module_name) httpParams = httpParams.set('module_name', params.module_name);
    return this.http
      .get<unknown>(this.basePath, {
        headers: tenantHeaders(tenantId),
        params: httpParams,
      })
      .pipe(map((raw) => this.normalizeList(raw)));
  }

  /** Permisos de un usuario (`GET /api/module-permissions/user/{userId}`). */
  listForUser(userId: string, tenantId?: string): Observable<ModulePermission[]> {
    const ctxTenant = resolveTenantId(this.serverContext);
    let httpParams = withTenantParam(ctxTenant) ?? new HttpParams();
    const filterTenant = (tenantId ?? '').trim();
    if (filterTenant) httpParams = httpParams.set('tenant_id', filterTenant);
    return this.http
      .get<unknown>(`${this.basePath}/user/${encodeURIComponent(userId)}`, {
        headers: tenantHeaders(ctxTenant),
        params: httpParams,
      })
      .pipe(map((raw) => this.normalizeList(raw)));
  }

  get(id: string): Observable<ModulePermission> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http
      .get<unknown>(`${this.basePath}/${id}`, {
        headers: tenantHeaders(tenantId),
        ...(p ? { params: p } : {}),
      })
      .pipe(map((raw) => this.normalizeOne(raw)));
  }

  create(payload: ModulePermissionCreatePayload): Observable<ModulePermission> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http
      .post<unknown>(this.basePath, payload, {
        headers: tenantHeaders(tenantId),
        ...(p ? { params: p } : {}),
      })
      .pipe(map((raw) => this.normalizeOne(raw)));
  }

  update(id: string, payload: ModulePermissionUpdatePayload): Observable<ModulePermission> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http
      .put<unknown>(`${this.basePath}/${id}`, payload, {
        headers: tenantHeaders(tenantId),
        ...(p ? { params: p } : {}),
      })
      .pipe(map((raw) => this.normalizeOne(raw)));
  }

  delete(id: string): Observable<void> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http.delete<void>(`${this.basePath}/${id}`, {
      headers: tenantHeaders(tenantId),
      ...(p ? { params: p } : {}),
    });
  }

  addFolder(
    permissionId: string,
    payload: FolderPermissionCreatePayload,
  ): Observable<FolderPermission> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http
      .post<unknown>(`${this.basePath}/${permissionId}/folders`, payload, {
        headers: tenantHeaders(tenantId),
        ...(p ? { params: p } : {}),
      })
      .pipe(map((raw) => this.normalizeFolder(raw)));
  }

  removeFolder(permissionId: string, folderId: string): Observable<void> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http.delete<void>(`${this.basePath}/${permissionId}/folders/${folderId}`, {
      headers: tenantHeaders(tenantId),
      ...(p ? { params: p } : {}),
    });
  }

  private normalizeList(raw: unknown): ModulePermission[] {
    if (Array.isArray(raw)) return raw.map((item) => this.normalizeOne(item));
    if (raw && typeof raw === 'object') {
      const items =
        (raw as Record<string, unknown>)['items'] ?? (raw as Record<string, unknown>)['data'];
      if (Array.isArray(items)) return items.map((item) => this.normalizeOne(item));
    }
    return [];
  }

  private normalizeOne(raw: unknown): ModulePermission {
    const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
    const foldersRaw = o['folder_permissions'];
    let folder_permissions: FolderPermission[] = [];
    if (Array.isArray(foldersRaw)) {
      folder_permissions = foldersRaw.map((f) => this.normalizeFolder(f));
    }
    return {
      id: String(o['id'] ?? ''),
      user_id: String(o['user_id'] ?? ''),
      tenant_id: String(o['tenant_id'] ?? ''),
      module_name: String(o['module_name'] ?? ''),
      is_enabled: o['is_enabled'] !== false,
      created_at: o['created_at'] != null ? String(o['created_at']) : undefined,
      updated_at: o['updated_at'] != null ? String(o['updated_at']) : null,
      user_email: o['user_email'] != null ? String(o['user_email']) : null,
      user_full_name: o['user_full_name'] != null ? String(o['user_full_name']) : null,
      tenant_name: o['tenant_name'] != null ? String(o['tenant_name']) : null,
      folder_permissions,
    };
  }

  private normalizeFolder(raw: unknown): FolderPermission {
    const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
    return {
      id: String(o['id'] ?? ''),
      module_permission_id:
        o['module_permission_id'] != null ? String(o['module_permission_id']) : undefined,
      project_id: typeof o['project_id'] === 'number' ? o['project_id'] : null,
      subfolder_path: o['subfolder_path'] != null ? String(o['subfolder_path']) : null,
      project_name: o['project_name'] != null ? String(o['project_name']) : null,
      created_at: o['created_at'] != null ? String(o['created_at']) : undefined,
    };
  }
}
